import React, { useEffect, useState } from 'react';
import {
  HomepageAboutDraft,
  ResolvedHomepageAbout,
  resolveHomepageAbout,
} from '../../utils/homepageAbout';
import './HomeAboutSection.css';

interface HomeAboutSectionProps {
  content: {
    heroDescription?: string;
    aboutDescription?: string;
    homepageAbout?: Parameters<typeof resolveHomepageAbout>[0]['homepageAbout'];
  };
  draft?: HomepageAboutDraft | null;
  sectionId?: string;
  preview?: 'desktop' | 'mobile';
}

const HomeAboutSection: React.FC<HomeAboutSectionProps> = ({
  content,
  draft = null,
  sectionId,
  preview,
}) => {
  const about: ResolvedHomepageAbout = resolveHomepageAbout(content, draft);
  const [imageFailed, setImageFailed] = useState(false);
  const portraitUrl = about.portrait?.imageUrl || '';
  useEffect(() => {
    setImageFailed(false);
  }, [portraitUrl]);
  const showImage = about.hasPortrait && !imageFailed && !!portraitUrl;
  const headingId = sectionId ? `${sectionId}-heading` : undefined;
  const aspect = about.portrait?.width && about.portrait?.height
    ? `${about.portrait.width} / ${about.portrait.height}`
    : '3 / 4';

  return (
    <section
      id={sectionId}
      className={`home-about${preview ? ' is-preview' : ''}`}
      data-layout={showImage ? 'image' : 'text'}
      data-preview={preview}
      aria-labelledby={about.heading.trim() && headingId ? headingId : undefined}
      aria-label={about.heading.trim() ? undefined : 'About Carmen Lorraine'}
      style={{
        '--about-focal-desktop': `${about.desktopPosition.x}% ${about.desktopPosition.y}%`,
        '--about-focal-mobile': `${about.mobilePosition.x}% ${about.mobilePosition.y}%`,
        '--about-aspect': aspect,
      } as React.CSSProperties}
    >
      <div className="home-about-layout">
        {showImage && about.portrait && (
          <figure className="home-about-portrait">
            <img
              src={portraitUrl}
              alt={about.portrait.altText || 'Carmen Lorraine'}
              width={about.portrait.width}
              height={about.portrait.height}
              onError={() => setImageFailed(true)}
            />
          </figure>
        )}
        <div className="home-about-copy">
          {about.eyebrow.trim() && <p className="home-about-eyebrow">{about.eyebrow}</p>}
          {about.heading.trim() && (
            <h2 id={headingId} className="home-about-heading">{about.heading}</h2>
          )}
          {about.introductionHtml && (
            <div
              className="home-about-intro"
              dangerouslySetInnerHTML={{ __html: about.introductionHtml }}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default HomeAboutSection;
